import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, Globe } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { GlassCard } from '../components/ui/GlassCard';
import { CITIES } from '../constants';

export const Locations = ({ onOpenQuote }: { onOpenQuote: () => void }) => {
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Mocking status for demonstration (in a real app, this would be in the data object)
  const getLocationStatus = (id: string) => {
    return id === 'chc' ? 'Licensee Operated' : 'Corporate HQ';
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 relative overflow-hidden">
      
      {/* Background Map Effect */}
      <div className="absolute inset-0 z-0 opacity-20 pointer-events-none">
         <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-neonBlue/10 rounded-full blur-[100px]"></div>
         {/* Abstract geometric map dots */}
         <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white rounded-full animate-pulse"></div>
         <div className="absolute top-1/3 left-1/2 w-2 h-2 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.5s'}}></div>
         <div className="absolute bottom-1/3 right-1/3 w-2 h-2 bg-white rounded-full animate-pulse" style={{ animationDelay: '1s'}}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-brand-neonBlue/10 border border-brand-neonBlue text-brand-neonBlue text-xs font-bold uppercase tracking-wider">
            <Globe size={12} /> Nationwide Coverage
          </div>
          <h1 className="text-4xl md:text-6xl font-display font-bold text-white mb-6">
            Where We Operate
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            From our headquarters in Auckland to our network of trusted licensees across the country, QuikPix delivers a consistent premium experience nationwide.
          </p>
        </div>

        {/* Location Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          {Object.values(CITIES).map((city) => (
            <Link key={city.id} to={`/location/${city.slug}`} className="group block h-full">
              <GlassCard hoverEffect className="h-full relative overflow-hidden flex flex-col">
                
                {/* City Image Header */}
                <div className="h-48 -mx-6 -mt-6 mb-6 relative overflow-hidden">
                   <img 
                      src={`https://source.unsplash.com/800x600/?${city.name},city`} 
                      alt={city.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      onError={(e) => {
                        // Fallback if unsplash source is tricky
                        (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?auto=format&fit=crop&q=80&w=800';
                      }}
                   />
                   <div className="absolute inset-0 bg-slate-900/40 group-hover:bg-slate-900/20 transition-colors"></div>
                   
                   {/* Status Badge */}
                   <div className="absolute top-4 right-4">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase border backdrop-blur-md ${
                        getLocationStatus(city.id) === 'Corporate HQ' 
                          ? 'bg-brand-neonBlue/20 border-brand-neonBlue text-brand-neonBlue' 
                          : 'bg-brand-neonPink/20 border-brand-neonPink text-brand-neonPink'
                      }`}>
                        {getLocationStatus(city.id)}
                      </span>
                   </div>
                </div>

                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-white group-hover:text-brand-neonBlue transition-colors">{city.name}</h2>
                    <div className="flex items-center gap-2 text-slate-400 text-sm mt-1">
                      <MapPin size={14} />
                      <span>North Island</span>
                    </div>
                  </div>
                </div>

                <p className="text-slate-400 text-sm mb-6 flex-1 line-clamp-3">
                  {city.seoDescription}
                </p>

                <div className="flex items-center justify-between pt-6 border-t border-white/5 mt-auto">
                   <div className="flex -space-x-2">
                      <div className="w-8 h-8 rounded-full border-2 border-slate-900 overflow-hidden">
                        <img src={city.operatorImage} alt="Operator" className="w-full h-full object-cover" />
                      </div>
                      <div className="w-8 h-8 rounded-full border-2 border-slate-900 bg-slate-800 flex items-center justify-center text-[10px] text-white font-bold">
                        +4
                      </div>
                   </div>
                   <span className="flex items-center gap-2 text-sm font-bold text-white group-hover:translate-x-1 transition-transform">
                     View Location <ArrowRight size={16} className="text-brand-neonBlue" />
                   </span>
                </div>

              </GlassCard>
            </Link>
          ))}

          {/* Coming Soon Card */}
          <GlassCard className="h-full flex flex-col items-center justify-center text-center p-8 border-dashed border-white/20 bg-transparent">
             <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center text-slate-500 mb-4">
               <MapPin size={32} />
             </div>
             <h3 className="text-xl font-bold text-white mb-2">Your City Here?</h3>
             <p className="text-slate-400 text-sm mb-6">
               We are actively looking for licensees in Queenstown, Hamilton, and Dunedin.
             </p>
             <Link to="/login">
               <Button variant="outline" size="sm">Become a Licensee</Button>
             </Link>
          </GlassCard>
        </div>

        {/* Global CTA */}
        <div className="bg-gradient-to-r from-brand-neonBlue/10 to-brand-neonPink/10 rounded-2xl p-12 text-center border border-white/10 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
          <h2 className="text-3xl font-display font-bold text-white mb-4">Ready to book nationwide?</h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            One contract, one point of contact. We handle multi-city roadshows with ease.
          </p>
          <Button size="lg" onClick={onOpenQuote}>Start a Multi-City Quote</Button>
        </div>

      </div>
    </div>
  );
};
